/**
 * Integrytas Group - Common Security Module (CSM)
 * Immutable top-right watermark — identical on every screen.
 *
 * Per brand standard:
 *   - text:    "[Application Name] by IntegrytasGroup"
 *   - color:   #9CA3AF (Integrytas neutral)
 *   - opacity: 0.20 (faint, professional)
 *
 * appName and brandingSuffix are resolved by the caller (useBranding) so this
 * component stays a pure presentational leaf with no side effects.
 *
 * DEMO OVERLAY. While the session reads the demo mirror, this also paints a
 * large faint DEMO across the page. It lives here rather than in its own
 * component deliberately: <Watermark> is already rendered by all nine surfaces
 * (the shell, mobile, the approval page and every auth screen), so putting the
 * mark here is what makes "on every screen" true by construction instead of by
 * remembering to add it to each one. That is the same reason the brand
 * watermark is a single component.
 *
 * It is aria-hidden, pointer-events-none and behind the content, so it cannot
 * intercept a click or be read out; and it is faint enough to sit over a dense
 * ERP grid without competing with the data. The red banner states the fact in
 * words — this makes a screenshot of any single screen self-identifying, which
 * a banner cropped out of frame does not.
 */

import { useTranslation } from 'react-i18next';
import { useDdmSchema } from '../../lib/ddm_mode';

interface WatermarkProps {
  appName: string;
  brandingSuffix: string;
}

function DemoOverlay() {
  const { t } = useTranslation();
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 flex select-none items-center justify-center overflow-hidden"
    >
      <span
        className="font-extrabold uppercase leading-none tracking-[0.2em] text-csm-danger"
        style={{
          // Sized to the viewport so it reads as a watermark on a phone and on
          // a wide ERP grid alike, without a media-query ladder.
          fontSize: 'clamp(4rem, 18vw, 16rem)',
          opacity: 0.06,
          transform: 'rotate(-24deg)',
        }}
      >
        {t('ddm.watermark', 'DEMO')}
      </span>
    </div>
  );
}

export function Watermark({ appName, brandingSuffix }: WatermarkProps) {
  const isDemo = useDdmSchema() === 'demo';
  return (
    <>
      {isDemo && <DemoOverlay />}
      <div
        aria-hidden
        className="pointer-events-none absolute right-3 top-1 select-none text-csm-xs tracking-wide text-csm-watermark"
        style={{ opacity: 0.2 }}
      >
        {appName} <span className="font-medium">{brandingSuffix}</span>
      </div>
    </>
  );
}
